/**
 * Notice: Some code was adapted from Material-UI's text field.
 *         Copyright (c) 2014 Call-Em-All (https://github.com/callemall/material-ui)
 */
import { FormControl, FormHelperText, Grid, InputBase, InputLabel, Chip as MUIChip } from '@mui/material';
import { FormHelperTextProps as FormHelperTextProperties } from '@mui/material/FormHelperText';
import { InputBaseProps } from '@mui/material/InputBase';
import { InputLabelProps as InputLabelProperties } from '@mui/material/InputLabel';
import { red } from '@mui/material/colors';
import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

const useStyle = makeStyles()({
  root: {
    width: '100%',
  },
  input: {
    '@media print': {
      display: 'none',
    },
  },
  label: {
    top: 4,
  },
  labelShrink: {
    top: 0,
  },
  helperText: {
    marginBottom: -20,
  },
  error: {
    '&:after': {
      backgroundColor: red.A400,
      transform: 'scaleX(1)',
    },
  },
});

export interface ChipRendererArgs<Chip> {
  text: React.ReactNode | string;
  chip: Chip;
  isDisabled: boolean;
  isFocused: boolean;
  handleClick: () => void;
  handleDelete: () => void;
}

export const defaultChipRenderer = <Chip,>({
  text,
  isFocused,
  isDisabled,
  handleClick,
  handleDelete,
}: ChipRendererArgs<Chip>) => (
  <MUIChip
    style={{ pointerEvents: isDisabled ? 'none' : undefined, backgroundColor: isFocused ? 'blue' : undefined }}
    onClick={handleClick}
    onDelete={handleDelete}
    label={text}
  />
);

interface Properties<Chip> {
  allowDuplicates: boolean;
  blurBehavior?: 'clear' | 'add' | 'ignore';
  className?: string;
  clearInputValueOnChange: boolean;
  dataSourceConfig?: {
    value: keyof Chip;
    text: keyof Chip;
  };
  disabled?: boolean;
  error?: boolean;
  FormHelperTextProps?: FormHelperTextProperties;
  fullWidth?: boolean;
  fullWidthInput?: boolean;
  helperText?: React.ReactNode;
  id?: React.ReactText;
  InputProps?: Partial<Omit<InputBaseProps, 'id'>>;
  inputValue?: string;
  InputLabelProps?: Partial<Omit<InputLabelProperties, 'htmlFor'>>;
  label?: React.ReactNode;
  placeholder?: string;
  required?: boolean;
  chips: Chip[];

  ref?: (element: HTMLDivElement | null) => void;

  renderChip: (args: ChipRendererArgs<Chip>) => React.ReactNode;

  onAdd: (chip: Chip) => void;
  onDelete?: (chip: Chip, index: number) => void;
  onEnter?: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

const ChipInput = <Chip,>(props: Properties<Chip>) => {
  const { classes } = useStyle();

  const [isFocused, setIsFocused] = useState(false);

  const [chips, setChips] = useState<Chip[]>([]);
  const [focusedChip, setFocusedChip] = useState<number | null>(null);
  const [inputValue, setInputValue] = useState(props.inputValue);
  const [clearInputValueOnChange] = useState(props.clearInputValueOnChange);
  // const [value] = useState(props.value);

  const actualInput = useRef<Window | undefined>(undefined);

  useEffect(() => {
    if (props.disabled) {
      setFocusedChip(null);
      return;
    }
    if (props.chips && clearInputValueOnChange) {
      if (inputValue) {
        props.InputProps?.onChange?.({ target: { value: '' } } as React.ChangeEvent<HTMLInputElement>);
        setInputValue('');
      }
    }
  }, [props.chips]);

  const focus = () => {
    actualInput.current?.focus();

    if (focusedChip) {
      setFocusedChip(null);
    }
  };

  const handleInputBlur: InputBaseProps['onBlur'] = (event) => {
    props.InputProps?.onBlur?.(event);

    setIsFocused(false);

    if (focusedChip) {
      setFocusedChip(null);
    }
  };

  const handleInputFocus: InputBaseProps['onFocus'] = (event) => {
    setIsFocused(true);

    props.InputProps?.onFocus?.(event);
  };

  const handleKeyDown: InputBaseProps['onKeyDown'] = (event) => {
    if (props.InputProps?.onKeyDown) {
      // Needed for arrow controls on menu in autocomplete scenario
      props.InputProps.onKeyDown(event);
      // Check if the callback marked the event as isDefaultPrevented() and skip further actions
      // enter key for example should not always add the current value of the inputField
      if (event.isDefaultPrevented()) {
        return;
      }
    }
    const relevantChips = props.chips ?? chips;
    if (event.key === 'Backspace' || event.key === 'Delete') {
      if ((event.target as HTMLInputElement).value !== '') {
        return;
      }
      if (focusedChip === null && event.key === 'Backspace') {
        setFocusedChip(relevantChips.length - 1);
      } else if (focusedChip !== null) {
        const value = relevantChips[focusedChip];
        handleDeleteChip(value, focusedChip);
        if (event.keyCode === 8 && focusedChip > 0) {
          setFocusedChip(focusedChip - 1);
        }
      }
    } else if (event.key === 'ArrowLeft') {
      if (focusedChip === null && (event.target as HTMLInputElement).value === '' && relevantChips.length) {
        setFocusedChip(relevantChips.length - 1);
      } else if (focusedChip !== null && focusedChip > 0) {
        setFocusedChip(focusedChip - 1);
      }
    } else if (event.key === 'ArrowRight') {
      if (focusedChip !== null && focusedChip < relevantChips.length - 1) {
        setFocusedChip(focusedChip + 1);
      } else {
        setFocusedChip(null);
      }
    } else {
      setFocusedChip(null);
    }
  };

  const handleUpdateInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!props.inputValue) {
      setInputValue(e.target.value);
    }
    props.InputProps?.onChange?.(e);
  };

  const handleDeleteChip = (chip: Chip, i: number) => {
    if (props.chips) {
      if (typeof chip === 'object') {
        props.onDelete?.(chip, i);
      }
    } else {
      chips.splice(i, 1); // remove the chip at index i

      if (focusedChip !== null) {
        if (focusedChip === i) {
          setFocusedChip(null);
        } else if (focusedChip > i) {
          setFocusedChip(focusedChip - 1);
        }
      }

      setChips([...chips]);
    }
  };

  const setActualInputRef = (ref: Window) => {
    actualInput.current = ref;
  };

  const stateOrPropChips = props.chips ?? chips;
  const chipInputValue = props.InputProps?.onChange ? props.inputValue : inputValue;
  const hasInput = chips.length > 0 || (chipInputValue?.length ?? 0) > 0;
  const shrinkFloatingLabel = props.label !== null && (hasInput || isFocused);

  return (
    <FormControl
      variant="standard"
      fullWidth={props.fullWidth}
      className={clsx(props.className, classes.root)}
      error={!!props.error}
      required={props.required}
      onClick={focus}
      disabled={props.disabled}
      ref={props.ref}
    >
      {props.label && (
        <InputLabel
          htmlFor={String(props.id)}
          classes={{ root: classes.label, shrink: classes.labelShrink }}
          shrink={shrinkFloatingLabel}
          focused={isFocused}
          {...props.InputLabelProps}
        >
          {props.label}
        </InputLabel>
      )}
      <Grid container spacing={1} marginTop={-0.5}>
        {stateOrPropChips.map((chip, i) => {
          return (
            <Grid item key={i}>
              {props.renderChip({
                chip,
                text: typeof chip === 'object' && chip !== null && 'text' in chip ? chip.text : '',
                isDisabled: !!props.disabled,
                isFocused: focusedChip === i,
                handleClick: () => setFocusedChip(i),
                handleDelete: () => handleDeleteChip(chip, i),
              })}
            </Grid>
          );
        })}
        <Grid item>
          <InputBase
            multiline
            classes={{ input: classes.input }}
            id={String(props.id)}
            value={chipInputValue}
            inputRef={setActualInputRef}
            disabled={props.disabled}
            fullWidth={props.fullWidthInput}
            placeholder={!hasInput && (shrinkFloatingLabel || !props.label) ? props.placeholder : undefined}
            {...props.InputProps}
            onKeyDown={handleKeyDown}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onChange={handleUpdateInput}
          />
        </Grid>
      </Grid>
      {props.helperText && (
        <FormHelperText
          {...props.FormHelperTextProps}
          className={
            props.FormHelperTextProps
              ? clsx(props.FormHelperTextProps.className, classes.helperText)
              : classes.helperText
          }
        >
          {props.helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default ChipInput;
